<template>
    <section class="content-block">
        <div class="vlx-container">
            <div>
                <div class="mb-7 sm:mb-14 sm:text-center max-w-2xl sm:mx-auto">
                    <h2><slot name="headline"></slot></h2>
                    <div class="text-editor">
                        <slot name="body"></slot>
                    </div>
                </div>

                <div v-if="accordionItems"
                     class="sm:flex sm:flex-nowrap items-start mb-4 sm:mb-12 last:mb-0">
                    <!-- image wrapper -->
                    <div class="relative mb-2 sm:mr-8 mx-auto sm:ml-0 lg:mr-16 sm:flex-1 lg:flex-auto w-full lg:max-w-lg">
                        <div v-for="(item, index) in accordionItems"
                             :key="index"
                             role="button"
                             tabindex="0"
                             @click="openAccordion(index)"
                             @keyup.enter.prevent="openAccordion(index)"
                             :style="`left: ${item.left}%; top: ${item.top}%;`"
                             class="absolute group z-10 rounded-full focus:outline-none focus:ring-2 focus:ring-velux-blue focus:ring-offset-2">
                            <span class="relative w-10 h-10 border-2 border-color-velux-red text-lg inline-flex items-center justify-center rounded-full z-10 group-hover:bg-velux-red group-hover:text-velux-white transition-color duration-150"
                                  :class="{ 'bg-velux-red text-velux-white': item.isOpen, 'bg-velux-white text-velux-red': !item.isOpen }">
                                {{ index + 1 }}
                            </span>
                            <span class="absolute -left-1 -right-1 -top-1 -bottom-1 w-12 h-12 bg-velux-white z-10 rounded-full opacity-25"></span>
                        </div>

                        <span class="overflow-hidden block aspect-w-6 aspect-h-5">
                            <slot name="image"></slot>
                        </span>
                    </div>
                    <!-- / image wrapper -->
                    <!-- DESKTOP accordion block wrapper -->
                    <div class="hidden sm:block sm:flex-1 bg-velux-white text-velux-grey-900">
                        <!-- accordion item wrapper -->
                        <div v-for="(item, index) in accordionItems" :key="index">
                            <div role="heading" aria-level="3">
                                <div role="button"
                                     tabindex="0"
                                     :aria-expanded="item.isOpen"
                                     :aria-controls="`accordion-panel-${index + id}`"
                                     :id="`accordion-trigger-${index + id}`"
                                     @click="openAccordion(index)"
                                     @keyup.enter.prevent="openAccordion(index)"
                                     :class="{ 'pointer-events-none': item.isOpen }"
                                     class="flex items-center px-3 py-4 border-b-2 border-velux-grey-100 group focus:outline-none focus:ring-2 focus:ring-velux-blue focus:ring-offset-1 transition-all duration-150">
                                    <span class="inline-flex items-center">
                                        <span class="w-8 h-8 inline-flex items-center mr-6 justify-center text-lg border-2 rounded-full flex-shrink-0 group-hover:bg-velux-red group-hover:text-velux-white group-hover:border-velux-red transition-color duration-150"
                                              :class="item.isOpen ? 'text-velux-white bg-velux-red border-velux-red' : 'border-velux-grey-200'">
                                            {{ index + 1 }}
                                        </span>
                                        <span class="text-left lg:text-xl"
                                              :class="{ 'font-bold': item.isOpen }">
                                            {{ item.trigger }}
                                        </span>
                                    </span>
                                    <div :class="item.isOpen ? 'transform rotate-180 pr-5' : ''"
                                         class="ml-auto">
                                        <i class="far fa-chevron-down fa-sm"></i>
                                    </div>
                                </div>
                            </div>
                            <div :id="`accordion-panel-${index + id}`"
                                 :aria-hidden="!item.isOpen"
                                 :aria-labelledby="`accordion-trigger-${index + id}`"
                                 :class="item.isOpen ? 'py-4 max-h-open-accordion' : 'max-h-0 py-0 invisible'"
                                 class="bg-velux-grey-100 px-4 duration-100 ease-out transition-all overflow-hidden text-editor"
                                 v-html="item.content"></div>
                        </div>
                        <!-- /accordion item wrapper -->
                    </div>
                    <!-- /DESKTOP accordion block wrapper -->
                    <!-- MOBILE accordion block wrapper -->
                    <div class="sm:hidden">
                        <!-- accordion item wrapper -->
                        <div class="relative">
                            <div role="heading" aria-level="3">
                                <button @click.prevent="toggleListBox"
                                        class="flex w-full items-center py-2 px-5 border-2 border-velux-grey-200 bg-velux-white text-velux-grey-900 mb-2"
                                        aria-haspopup="listbox"
                                        :aria-expanded="isListBoxOpen">
                                    <span class="w-8 h-8 inline-flex items-center mr-5 justify-center text-lg border-2 rounded-full text-velux-white bg-velux-red border-velux-red flex-shrink-0">
                                        {{ activeAccordionIndex + 1 }}
                                    </span>
                                    <span class="font-bold text-left">
                                        {{ activeAccordion.trigger }}
                                    </span>

                                    <div :class="isListBoxOpen ? 'transform rotate-180 pr-5' : 'pl-5'"
                                         class="ml-auto">
                                        <i class="far fa-chevron-down fa-sm"> </i>
                                    </div>
                                </button>
                            </div>
                            <div ref="listBox"
                                role="listbox"
                                id="listbox-options"
                                class="absolute left-0 right-0 border-2 border-velux-grey-100 -mt-3 z-10 shadow-md p-0"
                                :class="{ invisible: !isListBoxOpen, 'bottom-full': isListBoxUpwards }"
                                :aria-hidden="isListBoxOpen"
                                :aria-activedescendant="`listbox-options-${activeAccordionIndex + id}`">
                                <div v-for="(item, index) in accordionItems"
                                    :key="index"
                                    role="option"
                                    @keyup.enter.prevent="openAccordion(index)"
                                    @click="openAccordion(index)"
                                    class="py-2 px-5 bg-velux-white hover:bg-velux-grey-100 text-velux-grey-900 flex items-center cursor-pointer border-t-2 border-velux-grey-100"
                                    :id="`listbox-options-${index + id}`"
                                    :aria-selected="item.isOpen"
                                    tabindex="0">
                                    <span class="w-8 h-8 inline-flex items-center flex-shrink-0 mr-5 justify-center text-lg border-2 rounded-full"
                                          :class="item.isOpen ? 'text-velux-white bg-velux-red border-velux-red' : 'text-velux-red bg-velux-white border-velux-red'">
                                        {{ index + 1 }}
                                    </span>
                                    {{ item.trigger }}
                                </div>
                            </div>
                        </div>
                        <div class="bg-velux-grey-100 text-velux-grey-900 px-5 py-4 relative overflow-hidden">
                            <div v-for="(item, index) in accordionItems"
                                 :key="index"
                                 :aria-hidden="!item.isOpen"
                                 :aria-labelledby="`listbox-options-${index + id}`"
                                 :class="item.isOpen ? 'opacity-100 relative' : 'opacity-0 absolute invisible'"
                                 class="text-editor duration-100 ease-linear transition-opacity"
                                 v-html="item.content"></div>
                        </div>
                    </div>
                    <!-- /MOBILE accordion block wrapper -->
                </div>
                <div class="flex justify-center">
                    <slot name="cta"></slot>
                </div>
            </div>
        </div>
    </section>
</template>
<script>
    export default {
        name: 'product-highlight',
        data() {
            return {
                id: '',
                activeAccordionIndex: 0,
                accordionItems: [],
                isListBoxOpen: false,
                isListBoxUpwards: false
            };
        },
        created() {
            this.accordionItems = window.document.phsAccordions;
            window.document.phsId ? this.id = window.document.phsId : this.id = '';
            this.accordionItems[this.activeAccordionIndex].isOpen = true;
        },
        computed: {
            activeAccordion() {
                return this.accordionItems[this.activeAccordionIndex]
           }
        },
        methods: {
            isListBoxInViewport() {
                return (
                    window.innerHeight > this.$refs.listBox.getBoundingClientRect().bottom
                );
            },
            openAccordion(index) {
                this.activeAccordion.isOpen = false;
                this.activeAccordionIndex = index;
                this.accordionItems[index].isOpen = true;
                this.closeListBox();
            },
            toggleListBox() {
                if (this.isListBoxOpen) {
                    this.closeListBox();
                } else {
                    this.isListBoxOpen = true;
                    if (!this.isListBoxInViewport()) {
                        this.isListBoxUpwards = true;
                    }
                    this.$nextTick(() => {
                        this.$refs.listBox.focus();
                    });
                }
            },
            closeListBox() {
                this.isListBoxOpen = false;
                this.isListBoxUpwards = false;
            }
        }
    };
</script>
